import React, { useMemo } from 'react';
import { defineMessages, useIntl } from 'react-intl';
import { faArrowTrendUp } from '@fortawesome/pro-light-svg-icons/faArrowTrendUp';
import { faBolt } from '@fortawesome/pro-light-svg-icons/faBolt';
import { TypedMemo } from '../../../common/types';
import { SMLRoutes } from '../../../../routing/sml/SMLRoutes';
import { SMLResultsCard } from '../SMLResultsCard/SMLResultsCard';
import { SMLResultsItem } from '../SMLResultsCard/props';
import { useSMLPaginationParam } from '../../hooks/useSMLPaginationParam';
import { useSMLDatePickerDateParam } from '../../hooks/useSMLDatePickerDateParam';
import { useTrendsSection } from '../../hooks/useTrendsSection';
import { TrendsSectionCardProps } from './props';

const messages = defineMessages({
  pctChange30d: { id: 'app.sml.trends.pctChange30d', defaultMessage: '30d: {value}' },
});

const formatPct = (value: number): string => `${value >= 0 ? '+' : ''}${(value * 100).toFixed(1)}%`;

/**
 * One trend list — "trending up" or "unusual activity", for artists or tracks.
 * Shared by the Trends screen and the Home panes so both read the same rows.
 */
const TrendsSectionCardComponent: React.FC<TrendsSectionCardProps> = ({
  title,
  category,
  entityType,
  paginationParamName,
  limit,
}) => {
  const intl = useIntl();
  const { currentPage } = useSMLPaginationParam(paginationParamName);
  const { selectedDate, selectedDateValue, rangeDays } = useSMLDatePickerDateParam();
  const { rows, hasMore, isLoading, error, refetch, offset } = useTrendsSection(
    category,
    entityType,
    currentPage,
    rangeDays,
    selectedDateValue,
    limit
  );

  const detailsRoute = entityType === 'artist' ? SMLRoutes.artistDetails : SMLRoutes.trackDetails;
  const paramKey = entityType === 'artist' ? 'artistId' : 'trackId';

  const items: SMLResultsItem[] = useMemo(
    () =>
      rows.map((row, index) => ({
        id: row.id,
        name: row.name,
        imageUrl: row.imageUrl,
        position: offset + index + 1,
        rightElement: (
          <div className="d-flex flex-column align-items-end">
            <span className={`fw-semibold ${row.pctChange7d >= 0 ? 'text-success' : 'text-danger'}`}>
              {formatPct(row.pctChange7d)}
            </span>
            <span className="text-muted small">
              {intl.formatMessage(messages.pctChange30d, { value: formatPct(row.pctChange30d) })}
            </span>
          </div>
        ),
        chart: { entityType, date: selectedDate },
        route: detailsRoute,
        params: { [paramKey]: row.id },
      })),
    [rows, offset, detailsRoute, paramKey, entityType, selectedDate, intl]
  );

  // Green trend-up for "trending", violet spike for anomalies.
  const icon = category === 'trendingUp' ? faArrowTrendUp : faBolt;
  const iconColor = category === 'trendingUp' ? 'var(--pos)' : 'var(--brand-violet-lift)';

  return (
    <SMLResultsCard
      title={intl.formatMessage(title)}
      icon={icon}
      iconColor={iconColor}
      items={items}
      isLoading={isLoading}
      error={error}
      onRetry={() => {
        refetch().catch(console.error);
      }}
      paginationParamName={paginationParamName}
      hasMore={hasMore}
    />
  );
};

export const TrendsSectionCard = TypedMemo(TrendsSectionCardComponent);
